/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Settings {
  showBalance: boolean;
  notifications: {
    billReminders: boolean;
    goalUpdates: boolean;
    spendingAlerts: boolean;
    weeklyReports: boolean;
    monthlyReports: boolean;
  };
  privacy: {
    shareData: boolean;
    analytics: boolean;
    marketing: boolean;
  };
  theme: 'light' | 'dark' | 'auto';
  currency: string;
  dateFormat: string;
  monthChangeTrigger: number;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  toggleSetting: (key: keyof Settings, subKey?: string) => void;
  setMonthChangeTrigger: () => void;
  formatDate: (date: Date | string | any, options?: Intl.DateTimeFormatOptions) => string;
}

const defaultSettings: Settings = {
  showBalance: true,
  notifications: {
    billReminders: true,
    goalUpdates: true,
    spendingAlerts: false,
    weeklyReports: true,
    monthlyReports: true
  },
  privacy: {
    shareData: false,
    analytics: true,
    marketing: false
  },
  theme: 'auto',
  currency: 'USD',
  dateFormat: 'MM/DD/YYYY',
  monthChangeTrigger: 0
};

const SETTINGS_STORAGE_KEY = 'finflow-settings';
const THEME_STORAGE_KEY = 'finflow-theme';

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [monthChangeTrigger, setMonthChangeTrigger] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load settings from localStorage on mount and apply theme immediately
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      const root = document.documentElement;
      
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        const loadedSettings = {
          ...defaultSettings,
          ...parsedSettings,
          theme: (savedTheme as Settings['theme']) || parsedSettings.theme || defaultSettings.theme
        };
        setSettings(loadedSettings);
        
        // Apply theme immediately from loaded settings
        const theme = loadedSettings.theme || defaultSettings.theme;
        if (theme === 'dark') {
          root.classList.add('dark');
        } else if (theme === 'light') {
          root.classList.remove('dark');
        } else if (theme === 'auto') {
          // Auto theme - use system preference
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          if (mediaQuery.matches) {
            root.classList.add('dark');
          } else {
            root.classList.remove('dark');
          }
        }
      } else {
        // No saved settings - apply default theme immediately
        const theme = defaultSettings.theme;
        if (theme === 'dark') {
          root.classList.add('dark');
        } else if (theme === 'light') {
          root.classList.remove('dark');
        } else if (theme === 'auto') {
          // Auto theme - use system preference
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          if (mediaQuery.matches) {
            root.classList.add('dark');
          } else {
            root.classList.remove('dark');
          }
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Apply default theme on error
      const root = document.documentElement;
      const theme = defaultSettings.theme;
      if (theme === 'auto') {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        if (mediaQuery.matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    } finally {
      setIsHydrated(true);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
      localStorage.setItem(THEME_STORAGE_KEY, settings.theme);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }, [settings, isHydrated]);

  // Apply theme changes whenever theme setting changes
  useEffect(() => {
    const root = document.documentElement;
    
    if (settings.theme === 'dark') {
      root.classList.add('dark');
    } else if (settings.theme === 'light') {
      // Explicitly remove dark class and ensure it stays removed
      root.classList.remove('dark');
      // Force light mode by ensuring no dark class exists
      if (root.classList.contains('dark')) {
        root.classList.remove('dark');
      }
    } else if (settings.theme === 'auto') {
      // Auto theme - use system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      if (mediaQuery.matches) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      
      // Listen for system theme changes
      const handleChange = (e: MediaQueryListEvent) => {
        if (e.matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [settings.theme]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const triggerMonthChange = () => {
    setSettings(prev => ({ ...prev, monthChangeTrigger: prev.monthChangeTrigger + 1 }));
  };

  const toggleSetting = (key: keyof Settings, subKey?: string) => {
    setSettings(prev => {
      if (subKey && typeof prev[key] === 'object') {
        return {
          ...prev,
          [key]: {
            ...prev[key] as any,
            [subKey]: !(prev[key] as any)[subKey]
          }
        };
      }
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  // Date formatting utility function that respects user's date format preference
  const formatDate = (date: Date | string | any, options?: Intl.DateTimeFormatOptions): string => {
    try {
      let dateObj: Date;
      
      // Handle different date formats from Firestore
      if (date && typeof date === 'object' && 'toDate' in date) {
        // Firestore Timestamp
        dateObj = date.toDate();
      } else if (date instanceof Date) {
        // JavaScript Date object
        dateObj = date;
      } else if (typeof date === 'string') {
        // String date - handle timezone issues
        if (date.includes('T') && date.includes('Z')) {
          // ISO string with timezone - extract just the date part, NO conversion
          const dateOnly = date.split('T')[0]; // Get "2025-08-01" from "2025-08-01T00:00:00.000Z"
          
          // Parse the user's date format preference and format directly
          let formatOptions: Intl.DateTimeFormatOptions = {};
          switch (settings.dateFormat) {
            case 'DD/MM/YYYY':
              formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
              break;
            case 'MM/DD/YYYY':
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
              break;
            case 'YYYY-MM-DD':
              formatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
              break;
            case 'DD-MM-YYYY':
              formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
              break;
            case 'MM-DD-YYYY':
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
              break;
            default:
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
          }
          
          // Create a simple date object just for formatting, no timezone conversion
          const [year, month, day] = dateOnly.split('-').map(Number);
          const simpleDate = new Date(year, month - 1, day);
          return simpleDate.toLocaleDateString('en-US', formatOptions);
        } else {
          // Regular date string
          dateObj = new Date(date);
        }
      } else {
        return 'Invalid date';
      }

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid date';
      }

      // Parse the user's date format preference
      let formatOptions: Intl.DateTimeFormatOptions = {};
      
      switch (settings.dateFormat) {
        case 'DD/MM/YYYY':
          formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
          break;
        case 'MM/DD/YYYY':
          formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
          break;
        case 'YYYY-MM-DD':
          formatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
          break;
        case 'DD-MM-YYYY':
          formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
          break;
        case 'MM-DD-YYYY':
          formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
          break;
        default:
          formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
      }

      // Merge with any additional options passed in
      const finalOptions = { ...formatOptions, ...options };
      
      return dateObj.toLocaleDateString('en-US', finalOptions);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <SettingsContext.Provider value={{ 
      settings, 
      updateSettings, 
      toggleSetting,
      setMonthChangeTrigger: triggerMonthChange,
      formatDate
    }}>
      {children}
    </SettingsContext.Provider>
  );
};
